/* ---------------------------------------------------------------------------
   The critical path method, in working-day offsets.

   One forward pass and one backward pass, shared by the two things that solve a
   programme: freezing a baseline (./programme.ts) and forecasting from what has
   happened since (./forecast.ts). It is the vendored `Recompute()` in
   vendor/bridge-modeler/src/schedule/Schedule.ts, extended in the two ways the
   vendored one leaves room for and does not do (LC-43, LC-44):

     · ALL FOUR LINK TYPES. The vendored model stores SS, FF and SF and its
       solver skips them; a contractor's programme is full of them — the second
       pile cap starting three days after the first, the asphalt finishing four
       days after its base. Dropping them made a frozen plan earlier than the
       plan it was frozen from.
     · OFFSETS ARE WORKING DAYS. Nothing here knows about dates; the caller maps
       offsets to days through `CalendarIndex` (./calendar.ts). On a calendar
       where every day works the two are the same, and the vendored solver's
       answers come out unchanged.

   Finish offsets are EXCLUSIVE, as the vendored ones are: an activity of three
   days starting at 0 finishes at 3, which is the day its FS successor starts.

   The link rules, with `lag` in working days (negative is a lead):

     FS  successor start  ≥ predecessor finish + lag
     SS  successor start  ≥ predecessor start  + lag
     FF  successor finish ≥ predecessor finish + lag
     SF  successor finish ≥ predecessor start  + lag
   --------------------------------------------------------------------------- */

export type LinkType = 'FS' | 'SS' | 'FF' | 'SF';

export const LINK_TYPES: readonly LinkType[] = ['FS', 'SS', 'FF', 'SF'];

export function isLinkType(v: unknown): v is LinkType {
  return typeof v === 'string' && (LINK_TYPES as readonly string[]).includes(v);
}

export interface CpmNode {
  id: string;
  /** Working days. */
  duration: number;
  /** Start no earlier than this offset — the modeller's manual start. */
  notBefore: number | null;
  /** Nothing starts before this offset: the status date, in a forecast. */
  floor?: number;
  /**
   * Dates that are facts, not plan: an activity already started or finished.
   * Its logic is not applied to it (progress overrides logic) but it still
   * drives its successors.
   */
  fixed?: { es: number; ef: number } | null;
}

export interface CpmLink {
  from: string;
  to: string;
  type: LinkType;
  lag: number;
}

export interface CpmTimes {
  es: number;
  ef: number;
  ls: number;
  lf: number;
  /** Total float: late finish less early finish. Zero is critical. */
  tf: number;
}

export interface CpmResult {
  times: Map<string, CpmTimes>;
  /** The latest early finish. */
  finish: number;
  /** Nodes on a dependency cycle, solved as though they had no predecessors. */
  cycleNodes: string[];
}

/**
 * Solve. Links naming a node not in `nodes` are dropped, as the vendored
 * solver drops links to anything that is not a leaf.
 */
export function solveCpm(nodes: readonly CpmNode[], links: readonly CpmLink[]): CpmResult {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const edges = links.filter((l) => byId.has(l.from) && byId.has(l.to) && l.from !== l.to);

  const incoming = new Map<string, CpmLink[]>();
  const outgoing = new Map<string, CpmLink[]>();
  for (const n of nodes) {
    incoming.set(n.id, []);
    outgoing.set(n.id, []);
  }
  for (const e of edges) {
    incoming.get(e.to)!.push(e);
    outgoing.get(e.from)!.push(e);
  }

  /* Kahn's algorithm. Anything left with a non-zero in-degree sits on a cycle;
     the vendored solver tolerates those rather than refusing the schedule, and
     so does this. */
  const inDeg = new Map<string, number>();
  for (const n of nodes) inDeg.set(n.id, incoming.get(n.id)!.length);
  const order: string[] = [];
  const ready = nodes.map((n) => n.id).filter((id) => inDeg.get(id) === 0);
  while (ready.length > 0) {
    const id = ready.shift()!;
    order.push(id);
    for (const e of outgoing.get(id)!) {
      const k = (inDeg.get(e.to) ?? 0) - 1;
      inDeg.set(e.to, k);
      if (k === 0) ready.push(e.to);
    }
  }
  const cycleNodes = nodes.map((n) => n.id).filter((id) => (inDeg.get(id) ?? 0) > 0);
  const onCycle = new Set(cycleNodes);
  order.push(...cycleNodes);

  const dur = (id: string): number => Math.max(0, byId.get(id)!.duration);
  const es = new Map<string, number>();
  const ef = new Map<string, number>();

  for (const id of order) {
    const n = byId.get(id)!;
    if (n.fixed) {
      es.set(id, n.fixed.es);
      ef.set(id, Math.max(n.fixed.es, n.fixed.ef));
      continue;
    }
    const d = dur(id);
    let start = Math.max(n.notBefore ?? 0, n.floor ?? 0);
    if (!onCycle.has(id)) {
      for (const e of incoming.get(id)!) {
        const ps = es.get(e.from) ?? 0;
        const pf = ef.get(e.from) ?? 0;
        let driven: number;
        if (e.type === 'SS') driven = ps + e.lag;
        else if (e.type === 'FF') driven = pf + e.lag - d;
        else if (e.type === 'SF') driven = ps + e.lag - d;
        else driven = pf + e.lag;
        if (driven > start) start = driven;
      }
    }
    es.set(id, start);
    ef.set(id, start + d);
  }

  const finish = nodes.reduce((max, n) => Math.max(max, ef.get(n.id) ?? 0), 0);

  const ls = new Map<string, number>();
  const lf = new Map<string, number>();
  for (let i = order.length - 1; i >= 0; i -= 1) {
    const id = order[i]!;
    const d = dur(id);
    let late = finish;
    for (const e of outgoing.get(id)!) {
      const sls = ls.get(e.to) ?? finish;
      const slf = lf.get(e.to) ?? finish;
      let driven: number;
      if (e.type === 'SS') driven = sls - e.lag + d;
      else if (e.type === 'FF') driven = slf - e.lag;
      else if (e.type === 'SF') driven = slf - e.lag + d;
      else driven = sls - e.lag;
      if (driven < late) late = driven;
    }
    lf.set(id, late);
    ls.set(id, late - d);
  }

  const times = new Map<string, CpmTimes>();
  for (const n of nodes) {
    const e = ef.get(n.id) ?? 0;
    const l = lf.get(n.id) ?? finish;
    times.set(n.id, { es: es.get(n.id) ?? 0, ef: e, ls: ls.get(n.id) ?? 0, lf: l, tf: l - e });
  }
  return { times, finish, cycleNodes };
}
